/**
 * ReportFilters - Date range and granularity selection for reports
 *
 * Provides:
 * - Date range picker with preset options (This Year, Last Year, etc.)
 * - Custom date range selection
 * - Granularity toggle (Monthly/Quarterly/Yearly) for time-series reports
 */

import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import type { ReportGranularity } from '@/types/accounting';

interface ReportFiltersProps {
  /** Current granularity setting */
  granularity: ReportGranularity;
  /** Callback when granularity changes */
  onGranularityChange: (granularity: ReportGranularity) => void;
  /** Whether to show the granularity toggle */
  showGranularity?: boolean;
  /** Current date range */
  dateRange: { from: string; to: string };
  /** Callback when date range changes */
  onDateRangeChange: (range: { from: string; to: string }) => void;
}

const GRANULARITY_OPTIONS: { value: ReportGranularity; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

/**
 * Get date presets based on current year
 */
function getDatePresets() {
  const now = new Date();
  const year = now.getFullYear();
  const lastYear = year - 1;

  // Calculate 6 months ago
  const sixMonthsAgo = new Date(now);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0];

  return [
    {
      label: 'This Year',
      from: `${year}-01-01`,
      to: `${year}-12-31`,
    },
    {
      label: 'Last Year',
      from: `${lastYear}-01-01`,
      to: `${lastYear}-12-31`,
    },
    {
      label: 'Last 6 Months',
      from: sixMonthsAgoStr,
      to: now.toISOString().split('T')[0],
    },
    {
      label: 'Last 2 Years',
      from: `${year - 1}-01-01`,
      to: `${year}-12-31`,
    },
  ];
}

/**
 * Format date range for display
 */
function formatDateDisplay(from: string, to: string): string {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  return `${fromDate.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })} - ${toDate.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })}`;
}

export function ReportFilters({
  granularity,
  onGranularityChange,
  showGranularity = true,
  dateRange,
  onDateRangeChange,
}: ReportFiltersProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const datePresets = getDatePresets();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    }

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDatePicker]);

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Date Range Picker */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={clsx(
            'flex items-center gap-2 px-4 py-2',
            'bg-white border border-gray-300 rounded-lg',
            'text-sm text-gray-700',
            'hover:border-gray-400 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-blue-500'
          )}
        >
          <Calendar className="w-4 h-4 text-gray-400" />
          <span>{formatDateDisplay(dateRange.from, dateRange.to)}</span>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-gray-400 transition-transform',
              showDatePicker && 'rotate-180'
            )}
          />
        </button>

        {showDatePicker && (
          <div className="absolute top-full mt-2 left-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[280px]">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Quick Select
            </div>
            <div className="space-y-1 mb-4">
              {datePresets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => {
                    onDateRangeChange({ from: preset.from, to: preset.to });
                    setShowDatePicker(false);
                  }}
                  className={clsx(
                    'w-full text-left px-3 py-2 text-sm rounded-md',
                    'hover:bg-gray-100 transition-colors',
                    dateRange.from === preset.from &&
                      dateRange.to === preset.to &&
                      'bg-blue-50 text-blue-700'
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Custom Range
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) =>
                      onDateRangeChange({ ...dateRange, from: e.target.value })
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={(e) =>
                      onDateRangeChange({ ...dateRange, to: e.target.value })
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={() => setShowDatePicker(false)}
                className="mt-3 w-full px-3 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Granularity Toggle */}
      {showGranularity && (
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          {GRANULARITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => onGranularityChange(option.value)}
              className={clsx(
                'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                granularity === option.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
